import { useState, useEffect } from "react";
import { useAppTranslation } from "@/contexts/TranslationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Zap, MapPin, Clock, Users, CheckCircle, XCircle, Languages, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Referral {
  id: string | number;
  // Member Information
  memberMedicaidId: string;
  memberName: string;
  dateOfBirth: string;
  zipCode: string;
  preferredLanguage: string;

  // Health Plan Information
  healthPlan: string;
  planType: "Managed Care" | "Fee-for-Service";

  // Provider Information
  referringProvider: {
    name: string;
    npi: string;
    organization: string;
    phone: string;
  };

  // Clinical Context
  primaryDiagnosis: string;
  riskScore?: number;
  sdohNeeds: string[];
  urgencyLevel: "Immediate" | "Within 24h" | "Within 1 Week" | "Routine";

  // Authorization & Compliance
  authorizationRequired: boolean;
  consentObtained: boolean;
  hipaaCompliant: boolean;

  // Legacy fields for backward compatibility
  timeline: string;
  status: "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "Authorized" | "In Progress" | "On Hold" | "Completed";
  createdAt: string;
  timeAgo?: string;
}

interface LiveFeedProps {
  referrals: Referral[];
  onClaim: (id: string) => void;
  onReject: (id: string) => void;
  onBack?: () => void;
  onUpdateStatus?: (referralId: string, newStatus: string) => void;
  onViewDetails?: (referralId: string) => void;
  isAuthenticated?: boolean;
}

// Helper function to mask PII
const maskPII = (text: string, type: 'name' | 'id' | 'phone' | 'provider'): string => {
  if (type === 'name') {
    // Show first name initial + last name initial
    const parts = text.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}. ${parts[parts.length - 1][0]}.`;
    }
    return `${text[0]}. ${text[text.length - 1]}.`;
  }
  if (type === 'id') {
    // Show last 4 digits only
    if (text.length > 4) {
      return `****${text.slice(-4)}`;
    }
    return '****';
  }
  if (type === 'phone') {
    // Show area code only
    return `(${text.slice(0, 3)}) ***-****`;
  }
  if (type === 'provider') {
    // Mask provider name
    return '*** ***';
  }
  return '***';
};

export function LiveFeed({ referrals, onClaim, onReject, onBack, onUpdateStatus, onViewDetails, isAuthenticated = true }: LiveFeedProps) {
  const { t } = useAppTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [selectedReferralId, setSelectedReferralId] = useState<string | null>(null);
  const [hiddenReferralIds, setHiddenReferralIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Calculate time ago
  const getTimeAgo = (createdAt: string) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('liveFeed.justNow', 'Just now');
    if (diffInMinutes < 60) return `${diffInMinutes} ${t('liveFeed.minAgo', 'min ago')}`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}${t('liveFeed.hoursAgo', 'h ago')}`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}${t('liveFeed.daysAgo', 'd ago')}`;
  };

  // Filter to show pending referrals AND matched referrals awaiting acceptance (excluding hidden ones)
  const pendingReferrals = referrals.filter(r => {
    // Must not be hidden
    if (hiddenReferralIds.has(String(r.id))) return false;

    // Show Pending referrals (not yet matched)
    if (r.status === "Pending") return true;

    // Show Matched referrals that are awaiting acceptance (acceptanceStatus === 'pending' or undefined)
    if (r.status === "Matched") {
      // Type assertion to access PHI acceptance fields
      const ref = r as any;
      const acceptanceStatus = ref.acceptanceStatus;
      // Show if acceptance status is pending or not set yet
      return !acceptanceStatus || acceptanceStatus === 'pending';
    }

    return false;
  });

  const getUrgencyColor = (urgencyLevel: string) => {
    switch (urgencyLevel) {
      case "Immediate":
        return "border-l-red-500 bg-red-50";
      case "Within 24h":
        return "border-l-orange-500 bg-orange-50";
      case "Within 1 Week":
        return "border-l-yellow-500 bg-yellow-50";
      case "Routine":
        return "border-l-blue-500 bg-blue-50";
      default:
        return "border-l-gray-500 bg-gray-50";
    }
  };

  const handleAcceptReferral = (referralId: string) => {
    setSelectedReferralId(referralId);
    setShowAcceptDialog(true);
  };

  const confirmAcceptReferral = () => {
    if (selectedReferralId) {
      onClaim(selectedReferralId);
      if (onUpdateStatus) {
        onUpdateStatus(selectedReferralId, "Accepted");
      }
      // Hide the referral from the feed after acceptance
      setHiddenReferralIds(prev => new Set(prev).add(selectedReferralId));
      setShowAcceptDialog(false);
      setSelectedReferralId(null);
    }
  };

  const handlePassReferral = (referralId: string) => {
    setHiddenReferralIds(prev => new Set(prev).add(referralId));
    onReject(referralId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
          {/* Toolbar */}
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {onBack && (
              <Button
                variant="outline"
                onClick={onBack}
                className="flex items-center space-x-2 border border-v2border bg-surface text-ink hover:bg-paper-2 rounded-xl"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('buttons.back', 'Back')}</span>
              </Button>
            )}
            <div className="flex-1" />
            <p className="text-[13px] text-ink-500">
              {pendingReferrals.length} {t('liveFeed.pendingReferrals', 'pending referrals')} • {t('liveFeed.realTimeUpdates', 'Real-time updates')}
            </p>
          </div>

          {/* Live Feed Content */}
          <div className="space-y-6">
            {pendingReferrals.length === 0 ? (
              <Card className="border border-gray-200 shadow-sm bg-white rounded-xl overflow-hidden">
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <Zap className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('liveFeed.noPendingReferrals', 'No pending referrals')}</h3>
                  <p className="text-slate-600 text-lg">
                    {t('liveFeed.allProcessed', 'All referrals have been processed. New referrals will appear here in real-time.')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              pendingReferrals.map((referral) => (
                <Card
                  key={referral.id}
                  className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow bg-white rounded-xl overflow-hidden group"
                >
                  <CardContent className="p-4 md:p-5">
                    <div
                      className="flex items-start justify-between mb-4 cursor-pointer hover:bg-gray-50 -m-4 md:-m-5 p-4 md:p-5 rounded-t-xl transition-colors"
                      onClick={() => onViewDetails && onViewDetails(String(referral.id))}
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800">
                              {isAuthenticated ? referral.memberName : maskPII(referral.memberName, 'name')}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <Badge
                                className={
                                  referral.urgencyLevel === "Immediate"
                                    ? "bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300"
                                    : referral.urgencyLevel === "Within 24h"
                                    ? "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-300"
                                    : referral.urgencyLevel === "Within 1 Week"
                                    ? "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300"
                                    : "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300"
                                }
                              >
                                {referral.urgencyLevel}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-xs md:text-sm text-slate-600 mb-3">
                          <span className="flex items-center space-x-1 md:space-x-2">
                            <MapPin className="w-3 h-3 md:w-4 md:h-4 text-blue-500 flex-shrink-0" />
                            <span className="font-medium">{referral.zipCode}</span>
                          </span>
                          <span className="flex items-center space-x-1 md:space-x-2">
                            <Clock className="w-3 h-3 md:w-4 md:h-4 text-green-500 flex-shrink-0" />
                            <span className="font-medium">{getTimeAgo(referral.createdAt)}</span>
                          </span>
                          <span className="flex items-center space-x-1 md:space-x-2">
                            <Languages className="w-3 h-3 md:w-4 md:h-4 text-purple-500 flex-shrink-0" />
                            <span className="font-medium">{referral.preferredLanguage}</span>
                          </span>
                        </div>

                        {/* Additional info row - Mask PII when not authenticated */}
                        <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-slate-600 mb-3">
                          <span className="font-medium">
                            ID: {isAuthenticated ? referral.memberMedicaidId : maskPII(referral.memberMedicaidId, 'id')}
                          </span>
                          <span className="font-medium">Plan: {referral.healthPlan}</span>
                          <span className="font-medium">
                            Provider: {isAuthenticated ? referral.referringProvider.name : maskPII(referral.referringProvider.name, 'provider')}
                          </span>
                        </div>
                      </div>
                      {onViewDetails && (
                        <div className="ml-2 flex-shrink-0">
                          <Eye className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                      )}
                    </div>

                    <div className="mb-4 p-3 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-200/50 rounded-xl">
                      <div className="text-slate-600 font-semibold text-sm mb-2">{t('liveFeed.sdohNeeds', 'SDoH Needs')}:</div>
                      <div className="flex flex-wrap gap-2">
                        {referral.sdohNeeds.map((need, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border border-blue-200"
                          >
                            {need}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons - Only show when authenticated */}
                    {isAuthenticated ? (
                      <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                        <Button
                          onClick={() => handleAcceptReferral(String(referral.id))}
                          className="flex-1 bg-v2accent hover:bg-v2accent/90 text-white flex items-center justify-center space-x-2 py-2 md:py-3 text-sm md:text-base font-semibold shadow-sm hover:shadow-md transition-shadow rounded-lg active:scale-95 min-h-[44px]"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>{t('liveFeed.acceptReferral', 'Accept Referral')}</span>
                        </Button>
                        <Button
                          onClick={() => handlePassReferral(String(referral.id))}
                          variant="outline"
                          className="flex-1 bg-white border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center space-x-2 py-2 md:py-3 text-sm md:text-base font-semibold shadow-sm hover:shadow-md transition-shadow rounded-lg active:scale-95 min-h-[44px]"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>{t('liveFeed.pass', 'Pass')}</span>
                        </Button>
                      </div>
                    ) : (
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl text-center">
                        <p className="text-sm text-slate-600 mb-2">
                          {t('liveFeed.loginToClaimReferrals', 'To view full details and claim referrals, please log in')}
                        </p>
                        <Button
                          onClick={onBack}
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 text-sm font-semibold shadow-sm"
                        >
                          {t('liveFeed.loginSignUp', 'Login / Sign Up')}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
      </div>

      {/* Accept Referral Confirmation Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span>{t('liveFeed.referralAccepted', 'Referral Accepted')}</span>
              </DialogTitle>
              <DialogDescription>
                {t('liveFeed.acceptedDescription', 'You have successfully accepted this referral. The referral status has been updated to "Accepted" and will appear in your case workspace.')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAcceptDialog(false)}
                className="w-full sm:w-auto"
              >
                {t('buttons.close', 'Close')}
              </Button>
              <Button
                onClick={confirmAcceptReferral}
                className="w-full sm:w-auto bg-v2accent hover:bg-v2accent/90 text-white"
              >
                {t('buttons.confirm', 'Confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
}